import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, LogOut, Calendar, Users, ExternalLink, Copy, Edit3, Trash2, Circle, CheckCircle2, Files, Download, Upload, BookOpen, Archive, RotateCcw, Undo2, AlarmClock, ShieldCheck, KeyRound, QrCode, Layers, Link2 } from "lucide-react";
import { DeleteEventDialog, PurgeEventDialog } from "@/components/EventDeletion";
import { DeletionHistory } from "@/components/DeletionHistory";
import { AdminShell } from "@/components/AdminShell";

export default function AdminEventsList() {
  const { user, logout } = useAuth();
  const [events, setEvents] = useState(null);
  const [trash, setTrash] = useState([]);
  const [deletionLog, setDeletionLog] = useState([]);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Trash-panel state (soft-deleted events within the 24h recovery window)
  const [purgeTarget, setPurgeTarget] = useState(null);
  const navigate = useNavigate();

  async function load() {
    try {
      const [{ data: liveEvents }, { data: trashed }, { data: log }] = await Promise.all([
        api.get("/admin/events"),
        api.get("/admin/events-trash"),
        api.get("/admin/events-deletion-log"),
      ]);
      setDeletionLog(log || []);
      setEvents(liveEvents);
      setTrash(trashed || []);
    } catch {
      toast.error("Failed to load events");
    }
  }
  useEffect(() => { load(); }, []);

  function openDeleteDialog(ev) { setDeleteTarget(ev); }

  function onDeleted({ forever, archived, softDeletedId }) {
    const name = deleteTarget?.name;
    setDeleteTarget(null);
    load();
    if (forever || archived) return;
    toast.success(`"${name}" moved to trash — you have 24 hours to undo.`, {
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            await api.post(`/admin/events/${softDeletedId}/restore`);
            toast.success(`"${name}" restored.`);
            load();
          } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
        },
      },
      duration: 8000,
    });
  }

  async function restoreFromTrash(ev) {
    try {
      await api.post(`/admin/events/${ev.id}/restore`);
      toast.success(`"${ev.name}" restored.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    }
  }
  async function unarchiveEvent(ev) {
    try { await api.post(`/admin/events/${ev.id}/unarchive`); toast.success(`"${ev.name}" restored as draft`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  }

  function copyPublicLink(slug) {
    const url = `${window.location.origin}/events/${slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Public link copied"));
  }

  async function duplicate(ev, mode) {
    try {
      const { data } = await api.post(`/admin/events/${ev.id}/duplicate`, { mode });
      toast.success(`Duplicated as "${data.name}"`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Duplicate failed"); }
  }

  async function downloadTemplate() {
    try {
      const res = await api.get("/admin/event-template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "volmee-event-template.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  }
  async function handleImport(file) {
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/event-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported "${data.name}"`);
      load();
      navigate(`/admin/events/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  }

  return (
    <AdminShell>
      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Your events</div>
            <h1 className="mt-1 font-display text-4xl font-black tracking-tighter md:text-5xl">
              Event portfolio.
            </h1>
            <p className="mt-2 text-sm text-zinc-600">Create a new event or open an existing one to manage volunteers and allocations.</p>
          </div>
          <Link to="/admin/events/new" data-testid="new-event-btn" className="brand-btn inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold">
            <Plus className="h-4 w-4" /> New event
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2 border border-black/10 bg-white p-4">
          <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
            <div className="text-xs uppercase tracking-widest text-[#002FA7]">Bulk build from Excel</div>
            <p className="mt-1 text-xs text-zinc-500">Download the template, fill it with your days / sessions / roles, then upload it back to create an event in one shot.</p>
          </div>
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="inline-flex items-center gap-1.5 border border-black/20 bg-white px-3 py-2 text-xs hover:bg-black hover:text-white">
            <Download className="h-3.5 w-3.5" /> Download template
          </button>
          <button onClick={() => importRef.current?.click()} disabled={importing} data-testid="import-excel-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-2 text-xs font-semibold text-white hover:bg-white hover:text-black disabled:opacity-40">
            <Upload className="h-3.5 w-3.5" /> {importing ? "Importing…" : "Import from Excel"}
          </button>
          <input ref={importRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { handleImport(f); e.target.value = ""; } }} />
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          {events === null ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : events.length === 0 ? (
            <div className="col-span-full border border-dashed border-black/20 bg-white p-10 text-center">
              <Calendar className="mx-auto h-8 w-8 text-zinc-400" />
              <h3 className="mt-3 font-display text-xl font-black">No events yet</h3>
              <p className="mt-2 text-sm text-zinc-600">Click "New event" to set up your first one.</p>
            </div>
          ) : events.map((ev) => (
            <div key={ev.id} data-testid={`event-card-${ev.id}`} className="overflow-hidden border border-black/10 bg-white shadow-[6px_6px_0_0_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.08)]">
              {ev.cover_image ? (
                <img src={`${API_BASE}/public/events/${ev.slug}/cover-image`} alt={ev.name} className="block h-32 w-full object-cover" data-testid={`event-cover-${ev.id}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : null}
              <div className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {ev.status === "published" ? (
                      <span className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-emerald-800">
                        <CheckCircle2 className="h-3 w-3" /> Published
                      </span>
                    ) : ev.status === "archived" ? (
                      <span data-testid={`event-badge-archived-${ev.id}`} className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-amber-800">
                        <Archive className="h-3 w-3" /> Archived
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-zinc-700">
                        <Circle className="h-3 w-3" /> Draft
                      </span>
                    )}
                    <span className="text-[10px] font-mono text-zinc-400">/{ev.slug}</span>
                  </div>
                  <h3 className="mt-2 truncate font-display text-2xl font-black tracking-tighter" data-testid={`event-name-${ev.id}`}>{ev.name}</h3>
                  {ev.description ? <p className="mt-1 line-clamp-2 text-sm text-zinc-600">{ev.description}</p> : null}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 border-t border-black/5 pt-4 text-xs">
                <MiniStat label="Days" value={ev.days?.length || 0} />
                <MiniStat label="Sessions" value={(ev.days || []).reduce((a, d) => a + (d.sessions?.length || 0), 0)} />
                <MiniStat label="Slots" value={(ev.days || []).reduce((a, d) => a + (d.sessions || []).reduce((b, s) => b + (s.roles?.length || 0), 0), 0)} />
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500">
                <Users className="h-3 w-3" /> {ev.volunteer_count ?? 0} volunteer{(ev.volunteer_count ?? 0) === 1 ? "" : "s"}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-black/5 pt-4">
                <Link to={`/admin/events/${ev.id}`} data-testid={`open-event-${ev.id}`} className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-white hover:text-black">
                  Open dashboard
                </Link>
                <Link to={`/admin/events/${ev.id}/edit`} data-testid={`edit-event-${ev.id}`} className="inline-flex items-center gap-1 border border-black/20 px-3 py-1.5 text-xs hover:bg-black hover:text-white">
                  <Edit3 className="h-3 w-3" /> Edit
                </Link>
                <Link to={`/admin/events/${ev.id}/sign-on`} data-testid={`signon-event-${ev.id}`} className="inline-flex items-center gap-1 border border-[#002FA7]/40 px-3 py-1.5 text-xs text-[#002FA7] hover:bg-[#002FA7] hover:text-white">
                  <QrCode className="h-3 w-3" /> Sign-on
                </Link>
                {!ev.is_owner ? <span className="inline-flex items-center gap-1 border border-zinc-300 bg-zinc-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-zinc-700">Collaborator</span> : null}
                {ev.status === "published" ? (
                  <>
                    <button onClick={() => copyPublicLink(ev.slug)} title="Copy public link" aria-label="Copy public link" data-testid={`copy-link-${ev.id}`} className="inline-flex h-[30px] w-[30px] items-center justify-center border border-black/20 text-zinc-700 hover:bg-black hover:text-white"><Link2 className="h-3.5 w-3.5" /></button>
                    <a href={`/events/${ev.slug}`} target="_blank" rel="noreferrer" title="View public page" aria-label="View public page" data-testid={`view-public-${ev.id}`} className="inline-flex h-[30px] w-[30px] items-center justify-center border border-black/20 text-zinc-700 hover:bg-black hover:text-white"><ExternalLink className="h-3.5 w-3.5" /></a>
                  </>
                ) : null}
                {ev.is_owner ? (
                  <>
                    <button onClick={() => duplicate(ev, "without_volunteers")} title="Duplicate (without volunteers)" aria-label="Duplicate without volunteers" data-testid={`dup-without-vol-${ev.id}`} className="inline-flex h-[30px] w-[30px] items-center justify-center border border-black/20 text-zinc-700 hover:bg-black hover:text-white"><Files className="h-3.5 w-3.5" /></button>
                    <button onClick={() => duplicate(ev, "sessions_roles_only")} title="Duplicate (sessions &amp; roles only)" aria-label="Duplicate sessions and roles only" data-testid={`dup-sessions-roles-${ev.id}`} className="inline-flex h-[30px] w-[30px] items-center justify-center border border-black/20 text-zinc-700 hover:bg-black hover:text-white"><Layers className="h-3.5 w-3.5" /></button>
                    {ev.status === "archived" ? (
                      <button onClick={() => unarchiveEvent(ev)} title="Restore as draft" aria-label="Restore as draft" data-testid={`unarchive-event-${ev.id}`} className="inline-flex h-[30px] w-[30px] items-center justify-center border border-black/20 text-zinc-700 hover:bg-black hover:text-white"><RotateCcw className="h-3.5 w-3.5" /></button>
                    ) : null}
                    <button onClick={() => openDeleteDialog(ev)} data-testid={`delete-event-${ev.id}`} className="ml-auto inline-flex items-center gap-1 border border-red-600 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-600 hover:text-white">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </>
                ) : null}
              </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recycle bin — soft-deleted events within the 24h recovery window */}
        {trash.length > 0 ? (
          <section className="mt-12" data-testid="trash-panel">
            <div className="flex items-baseline justify-between border-t border-black/10 pt-8">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#FF3B30]">Recycle bin</div>
                <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">Recently deleted <span className="text-zinc-400">({trash.length})</span></h2>
                <p className="mt-1 text-xs text-zinc-500">Events sit here for 24 hours after deletion. Restore them any time before the countdown ends.</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {trash.map((ev) => (
                <TrashCard
                  key={ev.id}
                  ev={ev}
                  onRestore={() => restoreFromTrash(ev)}
                  onPurgeNow={() => setPurgeTarget(ev)}
                />
              ))}
            </div>
          </section>
        ) : null}
        <DeletionHistory rows={deletionLog} />
      </main>

      {deleteTarget ? (
        <DeleteEventDialog
          event={deleteTarget}
          isSuperAdmin={!!(user?.is_super_admin && user?._sa)}
          onClose={() => setDeleteTarget(null)}
          onDone={onDeleted}
        />
      ) : null}
      {purgeTarget ? (
        <PurgeEventDialog event={purgeTarget} onClose={() => setPurgeTarget(null)} onDone={() => { setPurgeTarget(null); load(); }} />
      ) : null}
    </AdminShell>
  );
}

function TrashCard({ ev, onRestore, onPurgeNow }) {
  // Countdown to the moment this event vanishes for good.
  const [countdown, setCountdown] = useState(() => msLeft(ev.recover_until));
  useEffect(() => {
    const t = setInterval(() => setCountdown(msLeft(ev.recover_until)), 30 * 1000);
    return () => clearInterval(t);
  }, [ev.recover_until]);
  const expired = countdown <= 0;
  return (
    <div data-testid={`trash-card-${ev.id}`} className="border border-black/15 bg-white p-4 shadow-[3px_3px_0_0_rgba(255,59,48,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">
            <Trash2 className="h-3 w-3" /> Deleted
            <span className="font-mono text-zinc-400">/{ev.slug}</span>
          </div>
          <h3 className="mt-1 truncate font-display text-xl font-black tracking-tighter" data-testid={`trash-name-${ev.id}`}>{ev.name}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-600">
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {ev.volunteer_count ?? 0} volunteer{(ev.volunteer_count ?? 0) === 1 ? "" : "s"}</span>
            {ev.deleted_purge_intent ? (
              <span className="inline-flex items-center gap-1 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-[#FF3B30]">Will purge volunteer data</span>
            ) : (
              <span className="inline-flex items-center gap-1 border border-black/10 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-black uppercase tracking-widest text-zinc-600">Volunteer data kept</span>
            )}
          </div>
        </div>
        <div className={`shrink-0 border px-2 py-1 text-right ${expired ? "border-red-300 bg-red-50 text-red-700" : "border-black/10 bg-zinc-50 text-zinc-700"}`} data-testid={`trash-countdown-${ev.id}`}>
          <div className="flex items-center justify-end gap-1 text-[9px] font-black uppercase tracking-widest">
            <AlarmClock className="h-3 w-3" /> {expired ? "About to purge" : "Purges in"}
          </div>
          <div className="mt-0.5 font-mono text-xs font-semibold">{expired ? "0h 0m" : formatCountdown(countdown)}</div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={onRestore} data-testid={`trash-restore-${ev.id}`} className="inline-flex items-center gap-1 border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white hover:text-emerald-700">
          <Undo2 className="h-3 w-3" /> Restore
        </button>
        <button onClick={onPurgeNow} data-testid={`trash-purge-now-${ev.id}`} className="inline-flex items-center gap-1 border border-red-300 px-3 py-1.5 text-xs text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600">
          <Trash2 className="h-3 w-3" /> Purge now
        </button>
      </div>
    </div>
  );
}

function msLeft(recoverUntilIso) {
  if (!recoverUntilIso) return 0;
  try { return Math.max(0, new Date(recoverUntilIso).getTime() - Date.now()); }
  catch { return 0; }
}
function formatCountdown(ms) {
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-medium uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-0.5 font-mono text-lg font-semibold">{value}</div>
    </div>
  );
}
